/* eslint-disable no-inner-declarations */
import { OrderModel } from '@/store/models/Order';
import { OrderStatusRejected } from '@/store/modules/order/types';
import { Comparator, compare } from '@/tools/compare';

import { allowsPassiveWithoutDevice } from '../hands/constants';
import { ProsthesisType } from '../prosthesis/constants';
import { isShoulderDisarticulationOrder } from './acroAssembly';
import { espireShowsExtraParameters, isEspireProsthesis } from './espire';

export enum OrderStep {
	New = 'order-new',
	Terminal = 'terminal-customization',
	Socket = 'socket-customization',
	HandType = 'hand-type',
	ExtraParameters = 'extra-parameters-customization',
	FinishingTypeSocket = 'socket-type-finishing-socket-details',
	CheckSocket = 'socket-check-socket-details',
	OuterSocket = 'socket-outer-socket-details',
	ColorPattern = 'color-pattern-customization',
	SkinGlove = 'skin-glove-customization',
	Elbow = 'elbow-customization',
	/** GLA-63: konfiguracja łokcia Espire (rozmiar, kolor, strona pokrętła). */
	EspireConfiguration = 'espire-configuration',
	ClientDetails = 'order-clients-details',
	Preview = 'order-preview',
	AfterConfirmation = 'after-confirmation',
	AddingScan = 'adding-scan',
	Alignment = 'order-alignment',
	Files = 'order-files',
	Details = 'order-details',
	ShippingAddress = 'order-shipping-address',
	Finalise = 'order-finalise',
	Feedback = 'order-feedback',
	SocketFittingFiles = 'order-socket-fitting-files',
	SocketFittingFinalise = 'order-socket-fitting-finalise',
}

const sharedPath: OrderStep[] = [
	OrderStep.ClientDetails,
	OrderStep.CheckSocket,
	OrderStep.Preview,
	OrderStep.AfterConfirmation,
	OrderStep.AddingScan,
	OrderStep.Alignment,
	OrderStep.Details,
	OrderStep.Files,
	OrderStep.Finalise,
	OrderStep.Feedback,
	OrderStep.SocketFittingFiles,
	OrderStep.SocketFittingFinalise,
];

const sharedPathAbove: OrderStep[] = [
	OrderStep.ClientDetails,
	OrderStep.OuterSocket,
	OrderStep.CheckSocket,
	OrderStep.Preview,
	OrderStep.AfterConfirmation,
	OrderStep.AddingScan,
	OrderStep.Details,
	OrderStep.Finalise,
	OrderStep.Feedback,
	OrderStep.SocketFittingFiles,
	OrderStep.SocketFittingFinalise,
];

/**
 * Ścieżka Espire (GLA-62, GLA-63).
 *
 * Ekran konfiguracji stoi PIERWSZY, zaraz po wybraniu modelu: rozmiar i kolor dotyczą łokcia,
 * więc protetyk ustala je, zanim przejdzie do ręki. Ten sam zestaw kroków obowiązuje w ścieżce
 * transhumeralnej i po wybraniu kapy Glaze Acro with A-LEX — Acro + A-LEX + Espire + ręka to
 * poprawna proteza, więc ekran nie może zależeć od ścieżki.
 */
const espirePath: OrderStep[] = [
	OrderStep.EspireConfiguration,
	OrderStep.HandType,
	// GLA-89: elektrody i bateria ręki mioelektrycznej.
	//
	// Bez tego kroku zamówienie Espire z ręką mioelektryczną szło na produkcję bez informacji,
	// jakie elektrody i jaką baterię dajemy (zgłosiła Alicja).
	//
	// Co robi z nim `pathForOrder()`, rozstrzyga WYŁĄCZNIE matryca produktu
	// (`espireShowsExtraParameters`): Classic krok traci, pozostałe trzy modele zachowują go
	// zawsze — także Classic Plus z ręką pasywną, którego wyprowadza stamtąd przekierowanie,
	// a nie filtr. Powód tej konstrukcji jest opisany przy samej regule.
	OrderStep.ExtraParameters,
	...sharedPathAbove,
];

export const orderPath: Record<ProsthesisType, OrderStep[]> = {
	[ProsthesisType.GlazeOneBelow]: [OrderStep.ColorPattern, OrderStep.Terminal, ...sharedPath],
	[ProsthesisType.GlazeKidsBelow]: [OrderStep.ColorPattern, OrderStep.Terminal, ...sharedPath],
	[ProsthesisType.WhizzlinkBelow]: [
		OrderStep.ColorPattern,
		OrderStep.Socket,
		OrderStep.ExtraParameters,
		...sharedPath,
	],
	[ProsthesisType.WhizzlinkKidsBelow]: [OrderStep.ColorPattern, OrderStep.Terminal, ...sharedPath],
	[ProsthesisType.WhizzlinkEcoBelow]: [OrderStep.ColorPattern, OrderStep.Terminal, ...sharedPath],
	[ProsthesisType.GlazeOneAbove]: [
		OrderStep.Elbow,
		OrderStep.ColorPattern,
		OrderStep.Terminal,
		...sharedPathAbove,
	],
	[ProsthesisType.WhizzlinkAbove]: [
		OrderStep.Elbow,
		OrderStep.ColorPattern,
		OrderStep.Socket,
		OrderStep.ExtraParameters,
		...sharedPathAbove,
	],
	[ProsthesisType.WhizzlinkEcoAbove]: [
		OrderStep.Elbow,
		OrderStep.ColorPattern,
		OrderStep.Terminal,
		...sharedPathAbove,
	],
	[ProsthesisType.NullProsthesis]: [
		OrderStep.Terminal,
		OrderStep.ClientDetails,
		OrderStep.Finalise,
	],
	// GLA-121: GBB dopuszcza rękę mioelektryczną według tej samej matrycy co GLA-89
	// (`espireShowsExtraParameters('glaze-basic-below') === true`), więc krok jest zawsze.
	// Ścieżka nie czyta stanu zamówienia; rękę pasywną wyprowadza przekierowanie, nie filtr.
	[ProsthesisType.GlazeBasicBelow]: [
		OrderStep.ColorPattern,
		OrderStep.HandType,
		OrderStep.ExtraParameters,
		...sharedPath,
	],

	// Espire (GLA-62). Sciezka PROWIZORYCZNA: dokument mowi "rece jak w Glaze Basic",
	// czyli krok HandType, ktorego dotychczasowe sciezki Above nie maja (uzywaja Terminal).
	// Lista dozwolonych typow rak per model weszla w GLA-85 (`handTypesForProsthesis`).
	// UWAGA: zdanie o ukryciu produktow bylo tu NIEAKTUALNE - od GLA-85 katalog ma
	// `is_available: true, is_hide: false`, czyli Espire jest widoczne i zamawialne.
	// Ta nieprawda podtrzymywala zalozenie "nie ma jeszcze zamowien Espire", na ktorym
	// omal nie oparlem pominiecia pozycji protezy w kolejce (wskazala Rita, GLA-63 r4).
	[ProsthesisType.EspireProAbove]: espirePath,
	[ProsthesisType.EspireHybridAbove]: espirePath,
	[ProsthesisType.EspireClassicPlusAbove]: espirePath,
	[ProsthesisType.EspireClassicAbove]: espirePath,
	// [ProsthesisType.GlazeBasicBelow]: [OrderStep.ColorPattern, OrderStep.HandType],

	[ProsthesisType.ErgoArmAbove]: [OrderStep.Terminal, ...sharedPathAbove],
	[ProsthesisType.ErgoArmPlusAbove]: [OrderStep.Terminal, ...sharedPathAbove],
	[ProsthesisType.ErgoArmHybridPlusAbove]: [
		OrderStep.Socket,
		OrderStep.ExtraParameters,
		...sharedPathAbove,
	],
	[ProsthesisType.ErgoArmElectronicPlusAbove]: [
		OrderStep.Socket,
		OrderStep.ExtraParameters,
		...sharedPathAbove,
	],
};

/**
 * Mapowanie slugów z bazy danych do ProsthesisType (z fallbackami)
 *
 * Zapewnia kompatybilność z różnymi wersjami slugów w bazie danych.
 * Fallbacki są dla starych slugów, które mogą jeszcze istnieć w bazie.
 */
const slugToProsthesisType: Record<string, ProsthesisType> = {
	// Below prostheses
	'glaze-one-below': ProsthesisType.GlazeOneBelow,
	'glaze-kids-below': ProsthesisType.GlazeKidsBelow,
	'whizzlink-below': ProsthesisType.WhizzlinkBelow,
	'whizzlink-kids-below': ProsthesisType.WhizzlinkKidsBelow,
	'whizzlink-eco-below': ProsthesisType.WhizzlinkEcoBelow,
	'glaze-basic-below': ProsthesisType.GlazeBasicBelow,

	// Above prostheses
	'glaze-one-above': ProsthesisType.GlazeOneAbove,
	'whizzlink-above': ProsthesisType.WhizzlinkAbove,
	'whizzlink-eco-above': ProsthesisType.WhizzlinkEcoAbove,

	// ErgoArm prostheses (poprawne slugi)
	'ergoarm-above': ProsthesisType.ErgoArmAbove,
	'ergoarm-plus-above': ProsthesisType.ErgoArmPlusAbove,
	'ergoarm-hybrid-plus-above': ProsthesisType.ErgoArmHybridPlusAbove,
	'ergoarm-electronic-plus-above': ProsthesisType.ErgoArmElectronicPlusAbove,
	'espire-pro-above': ProsthesisType.EspireProAbove,
	'espire-hybrid-above': ProsthesisType.EspireHybridAbove,
	'espire-classic-plus-above': ProsthesisType.EspireClassicPlusAbove,
	'espire-classic-above': ProsthesisType.EspireClassicAbove,

	// Terminal Device Only
	'null-prosthesis': ProsthesisType.NullProsthesis,

	// Fallbacki dla starych slugów ErgoArm (bezpieczeństwo)
	ergoarm: ProsthesisType.ErgoArmAbove,
	'ergoarm-plus': ProsthesisType.ErgoArmPlusAbove,
	'ergoarm-hybrid-plus': ProsthesisType.ErgoArmHybridPlusAbove,
	'ergoarm-electronic-plus': ProsthesisType.ErgoArmElectronicPlusAbove,
};

/**
 * Pobiera ścieżkę zamówienia dla slug z bazy danych.
 * Mapuje slug do ProsthesisType i zwraca odpowiednią ścieżkę z orderPath.
 *
 * @param slug - Slug produktu z bazy danych
 * @returns Tablica kroków zamówienia lub undefined jeśli slug nie jest rozpoznany
 */
export function getOrderPathForSlug(slug: string): OrderStep[] | undefined {
	const prosthesisType = slugToProsthesisType[slug];
	if (!prosthesisType) {
		return undefined;
	}
	return orderPath[prosthesisType];
}

export const rejectedOrderEditSteps: Record<OrderStatusRejected, OrderStep[]> = {
	rejected: [OrderStep.Preview],
	rejected_incorrect_scan: [OrderStep.AddingScan],
	rejected_incorrect_measurements: [OrderStep.ClientDetails],
	rejected_incorrect_attachments: [OrderStep.Files],
};

export namespace OrderStep {
	export function compareStepToOrder(
		step: OrderStep,
		comparator: Comparator,
		order: OrderModel,
	): boolean {
		const path = getOrderPathForSlug(order.prosthesis.slug);
		if (!path) {
			console.error(`Order path not defined for prosthesis slug: ${order.prosthesis.slug}`);
			return false;
		}
		const orderStep = order.step;
		const stepIndex = path.findIndex(val => val === step);
		const orderStepIndex = path.findIndex(val => val === orderStep);

		return compare(stepIndex, comparator, orderStepIndex);
	}

	/**
	 * Ścieżka zamówienia po odjęciu kroków, które dla NIEGO nie mają sensu.
	 *
	 * Jedno miejsce dla obu kierunków nawigacji — wcześniej te same cztery reguły stały
	 * zduplikowane w `getNextStep` i `getPreviousStep`, więc rozjazd kopii oznaczałby
	 * ścieżkę, której nie da się przejść w tę i z powrotem.
	 */
	export function pathForOrder(order: OrderModel): OrderStep[] | null {
		let path = getOrderPathForSlug(order.prosthesis.slug);

		if (!path) {
			console.error(`Order path not defined for prosthesis slug: ${order.prosthesis.slug}`);
			return null;
		}

		const includePetgSocket = order?.socket_detail_order?.include_petg_socket;

		if (!includePetgSocket) {
			path = path.filter(step => step !== OrderStep.Details);
			path = path.filter(step => step !== OrderStep.AfterConfirmation);
		} else if (includePetgSocket) {
			path = path.filter(step => step !== OrderStep.Files);
		}

		const petgHasChanges = order?.socket_detail_order?.petg_has_changes;

		if (!petgHasChanges && order.prosthesis?.prosthesis_category?.slug == 'glaze-below') {
			path = path.filter(step => step !== OrderStep.SocketFittingFiles);
		}

		if (order?.socket_detail_order?.outer_socket_type == 'fabricated_by_myself') {
			path = path.filter(step => step !== OrderStep.CheckSocket);
		}

		// WYŁUSZCZENIE W BARKU (ścieżka Glaze Acro): bez leja testowego i bez przymiarki —
		// zamówienie powstaje ze skanu (GLA-64). Reguła musi być JAWNA: lej robi Glaze
		// (`made_by_glaze`), więc warunek powyżej, oparty na „laminuję sam", już tu nie zadziała.
		//
		// BRAMKA NA KATEGORIĘ jest konieczna, nie ozdobna: `amputation_type` bywa NIEWYZEROWANY
		// na zamówieniu Below, gdy protetyk zmienił produkt po wypełnieniu szczegółów. Bez niej
		// takie zamówienie traciłoby przymiarkę, której ścieżka Below wymaga. Ten sam stan danych
		// ma już guard w karcie PDF (`OrderPreviewViewModel`) — raz wyprodukował błędny dokument.
		// Kategoria, nie dopasowanie tekstu w slugu — tak samo robi `OrderObserver`.
		if (isShoulderDisarticulationOrder(order)) {
			path = path.filter(step => step !== OrderStep.CheckSocket);
		}

		// GLA-89: elektrody i bateria dotyczą WYŁĄCZNIE ręki mioelektrycznej.
		//
		// Warunek zawężony do Espire świadomie: pozostałe ścieżki z tym krokiem (Whizzlink,
		// ErgoArm Hybrid/Electronic Plus) wybierają rękę bioniczną krokiem `Socket`, więc mają
		// ją zawsze — ogólny filtr „brak ręki bionicznej" mógłby im pominąć krok, zanim zdążą
		// ją zapisać. Przy Espire wybór jest warunkowy (krok `HandType`: bioniczna albo pasywna).
		//
		// SAMA REGUŁA — i powód, dla którego NIE WOLNO oprzeć jej na zapisanej ręce — żyje
		// w `espireShowsExtraParameters`; tam też jej testy.
		if (
			isEspireProsthesis(order.prosthesis?.slug) &&
			!espireShowsExtraParameters(order.prosthesis?.slug)
		) {
			path = path.filter(step => step !== OrderStep.ExtraParameters);
		}

		return path;
	}

	/**
	 * Krok, w którym protetyk WYBIERA rękę — czyli ten tuż przed elektrodami i baterią.
	 *
	 * Liczony ze ścieżki SUROWEJ, nie z `pathForOrder`: służy do wyprowadzenia protetyka
	 * z ekranu elektrod, na który wszedł BEZ ręki bionicznej, a w takim stanie `pathForOrder`
	 * ten krok właśnie odfiltrowało — `getPreviousStep` zwróciłby `null` i wyrzuciłby go na
	 * pulpit, gubiąc kontekst zamówienia.
	 *
	 * Dla Espire i Glaze Basic Below to `HandType`, dla Whizzlinka i ErgoArmów `Socket` — właśnie
	 * tam zapada wybór, którego brak go tu zatrzymał (GLA-89).
	 */
	export function handChoiceStepFor(order: OrderModel): OrderStep | null {
		const path = getOrderPathForSlug(order.prosthesis.slug);
		const index = path?.findIndex(val => val === OrderStep.ExtraParameters) ?? -1;

		if (!path || index <= 0) {
			return null;
		}

		return path[index - 1];
	}

	/**
	 * Czy `step` leży w ścieżce zamówienia PO kroku elektrod — czyli czy protetyk wraca.
	 */
	function comesFromLaterStep(order: OrderModel, step?: OrderStep | null): boolean {
		if (!step) {
			return false;
		}

		const path = pathForOrder(order);
		const here = path?.indexOf(OrderStep.ExtraParameters) ?? -1;
		const there = path?.indexOf(step) ?? -1;

		return here >= 0 && there > here;
	}

	/**
	 * Dokąd wyprowadzić protetyka, który trafił na ekran elektrod BEZ ręki bionicznej.
	 *
	 * `null` znaczy „zostaw go tutaj" — ekran ma czym się zająć.
	 *
	 * KIERUNEK BIERZE SIĘ Z KIERUNKU WĘDRÓWKI, nie ze stanu zamówienia. To nie jest subtelność:
	 * obaj recenzenci mieli rację jednocześnie i każdy chciał innej odpowiedzi.
	 *   - Wyjście zawsze WSTECZ kazałoby protetykowi z ręką pasywną przeklikać raz jeszcze wybór,
	 *     którego już dokonał, i zapętlało przejście „dalej" z wyboru ręki (Rita, r2).
	 *   - Wyjście zawsze DO PRZODU zabijało przycisk „Wstecz" na Client Details: cofnięcie
	 *     prowadziło na ten ekran, a stąd strażnik odsyłał z powrotem, więc przycisk nie robił nic
	 *     (Cerber i Rita niezależnie, r3).
	 * Krok źródłowy rozstrzyga oba przypadki naraz: wracającego puszczamy dalej wstecz, idącego
	 * naprzód — naprzód.
	 *
	 * Zamówienie, które nie wybrało ani ręki, ani terminala, wraca na krok wyboru, jeśli
	 * `allowsPassiveWithoutDevice` zwraca `false` — tam i tylko tam może ten brak uzupełnić.
	 *
	 * GLA-121: w GBB brak obu rekordów to dozwolony wybór ręki pasywnej bez urządzenia.
	 * Zachowujemy przejście sprzed zmiany, zgodnie z kierunkiem wędrówki.
	 */
	export function exitStepFromExtraParameters(
		order: OrderModel,
		fromStep?: OrderStep | null,
	): OrderStep | null {
		if (order.bionic_hands?.bionic_hand_id) {
			return null;
		}

		const handChoice = handChoiceStepFor(order);

		if (!order.terminals?.length && !allowsPassiveWithoutDevice(order.prosthesis?.slug)) {
			return handChoice;
		}

		if (comesFromLaterStep(order, fromStep)) {
			return handChoice;
		}

		return getNextStep(OrderStep.ExtraParameters, order) ?? handChoice;
	}

	export function getNextStep(step: OrderStep, order?: OrderModel): OrderStep | null {
		if (!order) {
			return null;
		}

		const path = pathForOrder(order);

		if (!path) {
			return null;
		}

		const currentIndex = path.findIndex(val => val === step);

		if (currentIndex === -1 || currentIndex === path.length - 1) {
			return null;
		}
		const nextStep = path[currentIndex + 1];
		return nextStep;
	}

	export function getPreviousStep(step: OrderStep, order?: OrderModel): OrderStep | null {
		if (!order) {
			return null;
		}

		const path = pathForOrder(order);

		if (!path) {
			return null;
		}

		const currentIndex = path.findIndex(val => val === step);
		const nextStep = path[currentIndex - 1];

		if (currentIndex === -1 || currentIndex === 0) {
			return null;
		}

		return nextStep;
	}
}

export const orderDetailsComponent: Record<ProsthesisType, string> = {
	[ProsthesisType.GlazeOneBelow]: 'DefaultDetails',
	[ProsthesisType.GlazeKidsBelow]: 'DefaultDetails',
	[ProsthesisType.WhizzlinkBelow]: 'WhizzlinkDetails',
	[ProsthesisType.WhizzlinkKidsBelow]: 'DefaultDetails',
	[ProsthesisType.WhizzlinkEcoBelow]: 'DefaultDetails',

	//Above
	[ProsthesisType.GlazeOneAbove]: 'DefaultDetails',
	[ProsthesisType.WhizzlinkAbove]: 'WhizzlinkDetails',
	[ProsthesisType.WhizzlinkEcoAbove]: 'DefaultDetails',
	[ProsthesisType.NullProsthesis]: 'DefaultDetails',
	[ProsthesisType.ErgoArmAbove]: 'DefaultDetails',
	[ProsthesisType.ErgoArmPlusAbove]: 'DefaultDetails',
	[ProsthesisType.ErgoArmHybridPlusAbove]: 'DefaultDetails',
	[ProsthesisType.ErgoArmElectronicPlusAbove]: 'DefaultDetails',

	[ProsthesisType.EspireProAbove]: 'DefaultDetails',
	[ProsthesisType.EspireHybridAbove]: 'DefaultDetails',
	[ProsthesisType.EspireClassicPlusAbove]: 'DefaultDetails',
	[ProsthesisType.EspireClassicAbove]: 'DefaultDetails',

	[ProsthesisType.GlazeBasicBelow]: 'DefaultDetails',
};

export const NULL_PROSTHESIS_ID = 10;

export const TERMINAL_DEVICE_ONLY_HIDE_COLOR_VARIANTS = ['according-to-chosen-color-combination'];
export const PROSTHETIST_CATEGORY_HIDE_COLOR_VARIANTS = [
	'cloudy-billy',
	'red-robin',
	'powder-pink',
	'bubble-gum',
	'jumbo-jungle',
	'polar-bear',
	'lolli-pop',
];
